import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const useClientLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export default function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map());
  const currentKey = useRef(location.key);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const previousScrollRestoration = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const saveCurrentPosition = () => {
      positions.current.set(currentKey.current, { x: window.scrollX || 0, y: window.scrollY || 0 });
      if (positions.current.size > 100) {
        positions.current.delete(positions.current.keys().next().value);
      }
    };
    window.addEventListener("scroll", saveCurrentPosition, { passive: true });
    return () => {
      window.removeEventListener("scroll", saveCurrentPosition);
      if ("scrollRestoration" in window.history) window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useClientLayoutEffect(() => {
    if (typeof window === "undefined") return;

    if (currentKey.current !== location.key) {
      positions.current.set(currentKey.current, { x: window.scrollX || 0, y: window.scrollY || 0 });
    }

    if (location.hash) {
      let targetId = location.hash.slice(1);
      try {
        targetId = decodeURIComponent(targetId);
      } catch {
        // Keep the raw fragment when it is not valid percent-encoding.
      }
      const target = document.getElementById(targetId);
      if (target) target.scrollIntoView();
    } else if (navigationType === "POP") {
      const position = positions.current.get(location.key) || { x: 0, y: 0 };
      window.scrollTo(position.x, position.y);
    } else {
      window.scrollTo(0, 0);
    }

    currentKey.current = location.key;
  }, [location.hash, location.key, location.pathname, location.search, navigationType]);

  return null;
}
